import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { SubjectRef } from '../../api/inspections';
import type { InspectorSummary, Inspector } from '../../api/inspectors';
import type { Subject } from '../../api/subjects';
import type { Page } from '../../api/types';
import type { AutocompleteSource } from '../types';

/** A record an AUTOCOMPLETE field can refer to. `label` is what the input and read-only views show. */
export interface ReferenceOption {
  id: string;
  label: string;
  hint?: string;
}

/** "Základná škola, Doľany (EDUID 100001179)": names repeat, so the municipality and EDUID tell them apart. */
export function subjectLabel(s: Pick<SubjectRef, 'name' | 'eduid' | 'municipality'>): string {
  return `${s.name}, ${s.municipality} (EDUID ${s.eduid})`;
}

/** Suggestions of a source register (subjects: name, EDUID or municipality; inspectors: name or e-mail). */
export function searchReferences(source: AutocompleteSource, q: string, signal: AbortSignal): Promise<ReferenceOption[]> {
  if (source === 'INSPECTOR') {
    return api<InspectorSummary[]>('/inspectors/search', { query: { q }, signal }).then((list) =>
      list.map((i) => ({ id: i.id, label: i.displayName, hint: i.email })),
    );
  }
  return api<Page<Subject>>('/subjects', { query: { q, page: 0, size: 10, sort: 'name,asc' }, signal }).then((page) =>
    page.content.map((s) => ({
      id: s.id,
      label: subjectLabel({ ...s, municipality: s.address.municipality }),
      hint: `EDUID ${s.eduid}, ${s.address.municipality}`,
    })),
  );
}

/**
 * The label of a stored reference (cached per id). `missing` when the record no longer exists (e.g. a deleted
 * inspector); while loading the label is undefined.
 */
export function useReferenceLabel(source: AutocompleteSource, id: string | null | undefined) {
  const query = useQuery({
    queryKey: ['reference', source, id],
    queryFn: async () => {
      if (source === 'INSPECTOR') return (await api<Inspector>(`/inspectors/${id}`)).displayName;
      const s = await api<Subject>(`/subjects/${id}`);
      return subjectLabel({ ...s, municipality: s.address.municipality });
    },
    enabled: Boolean(id),
    staleTime: 60_000,
    retry: false,
  });
  return { label: query.data, missing: query.isError };
}
